import { useState } from "react";
import { HttpStatusCode, isAxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { useApiClient } from "@/hooks/useApiClient";
import { isIpNotAllowedError } from "@/utils/ipRestriction";
import { MANUAL_SEARCH_ENDPOINT, postManualSearch } from "../api";
import { type ManualSearchError, type ManualSearchResult } from "../types";

export const MANUAL_SEARCH_ERROR_MESSAGE =
  "現在マニュアル検索を利用できません。時間をおいてもう一度お試しください。";
export const MANUAL_SEARCH_RATE_LIMIT_MESSAGE =
  "しばらく時間をおいてから、もう一度お試しください。";
export const MANUAL_SEARCH_MAX_MESSAGE_LENGTH = 1000;

export interface UseManualSearchResult {
  results: ManualSearchResult[] | null;
  errorMessage: string;
  isSubmitting: boolean;
  resetSearch: () => void;
  submitMessage: (message: string) => Promise<boolean>;
}

const manualSearchErrorMessage = (error: unknown): string =>
  isAxiosError(error) &&
  error.response?.status === HttpStatusCode.TooManyRequests
    ? MANUAL_SEARCH_RATE_LIMIT_MESSAGE
    : MANUAL_SEARCH_ERROR_MESSAGE;

const reportManualSearchError = (error: unknown): void => {
  const status = isAxiosError<ManualSearchError>(error)
    ? error.response?.status
    : undefined;
  if (status !== undefined && status < HttpStatusCode.InternalServerError) {
    return;
  }

  const requestId = isAxiosError<ManualSearchError>(error)
    ? error.response?.data?.requestId
    : undefined;
  Sentry.captureException(new Error("Manual search request failed"), {
    extra: {
      endpoint: MANUAL_SEARCH_ENDPOINT,
      status,
      requestId,
    },
  });
};

export const useManualSearch = (): UseManualSearchResult => {
  const { apiClient } = useApiClient();
  const [results, setResults] = useState<ManualSearchResult[] | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetSearch = (): void => {
    setResults(null);
    setErrorMessage("");
  };

  const submitMessage = async (message: string): Promise<boolean> => {
    const trimmedMessage = message.trim();
    if (
      trimmedMessage.length === 0 ||
      trimmedMessage.length > MANUAL_SEARCH_MAX_MESSAGE_LENGTH ||
      isSubmitting
    ) {
      return false;
    }

    setResults(null);
    setErrorMessage("");
    setIsSubmitting(true);
    try {
      setResults(await postManualSearch(apiClient, trimmedMessage));
      return true;
    } catch (error) {
      // IP拒否の案内は共通処理に任せ、利用不可の通知を重ねない。
      if (isIpNotAllowedError(error)) {
        return false;
      }
      reportManualSearchError(error);
      setErrorMessage(manualSearchErrorMessage(error));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    results,
    errorMessage,
    isSubmitting,
    resetSearch,
    submitMessage,
  };
};
